<template>
	<div :class="['table-container', locked && 'is-locked']">
		<table
			v-if="!collapse && sourceObjectRightFormat"
			class="table"
			:class="[
				tableMobilev1 ? 'table--mobile-v1' : '',
				paginationFeature ? 'is-pagination-feature-on' : '',
				tableMobilev2 ? 'table--mobile-v2' : '',
				tableNested ? 'table--nested' : ''
			]"
			:id="`${uid}_table`"
		>
			<slot name="col-group"></slot>
			<thead class="table__header">
				<tr class="table__tr">
					<th
						scope="col"
						class="table__th"
						v-for="(header, index) in sourceObject.headers"
						v-bind:key="index"
						v-on:click="sortBy"
						:sortBy="header.key"
						:isSorteable="header.sorteable"
						:id="index"
						data-id="th-sortBy"
					>
						<slot
							:name="`header-${header.key}`"
							:header="header.key"
						>
							<span>{{ header.label }}</span>
						</slot>
						<i
							v-if="header.sorteable && !showArrows(header.key)"
							class="icon icon-sort-set"
						></i>
						<slot v-if="header.sorteable && showArrows(header.key)">
							<i
								class="icon icon-sort-ascending"
								v-if="showUpArrow()"
							></i>
							<i
								class="icon icon-sort-descending"
								v-if="!showUpArrow()"
							></i>
						</slot>
					</th>
				</tr>
			</thead>
			<tbody class="table__body">
				<tr
					class="table__tr"
					:id="`${uid}_tr`"
					v-for="(row, index) in source.items"
					:key="index"
				>
					<td
						:id="`${uid}_td`"
						:data-title="header.label"
						class="table__td"
						v-for="(header, index2) in sourceObject.headers"
						:key="index2"
					>
						<slot
							:name="`body-${header.key}`"
							:body="row"
							:index="index"
						>
							<span class="table__data">{{
								row[header.key]
							}}</span>
						</slot>
					</td>
				</tr>
			</tbody>
		</table>
		<table
			v-else
			class="table"
			:class="[
				tableMobilev1 ? 'table--mobile-v1' : '',
				paginationFeature ? 'is-pagination-feature-on' : '',
				tableMobilev2 ? 'table--mobile-v2' : '',
				tableNested ? 'table--nested' : ''
			]"
			:id="`${uid}_table`"
		>
			<slot name="col-group"></slot>
			<thead class="table__header">
				<tr class="table__tr">
					<th
						scope="col"
						class="table__th table__th-trigger"
						v-bind:key="'expandCol'"
					></th>
					<th
						scope="col"
						class="table__th"
						v-for="(header, index) in sourceObject.headers"
						v-bind:key="index"
						v-on:click="sortBy"
						:sortBy="header.key"
						:isSorteable="header.sorteable"
						:id="index"
						data-id="th-sortBy"
					>
						<slot
							:name="`header-${header.key}`"
							:header="header.key"
						>
							<span>{{ header.label }}</span>
						</slot>
						<i
							v-if="header.sorteable && !showArrows(header.key)"
							class="icon icon-sort-set"
						></i>
						<slot v-if="header.sorteable && showArrows(header.key)">
							<i
								class="icon icon-sort-ascending"
								v-if="showUpArrow()"
							></i>
							<i
								class="icon icon-sort-descending"
								v-if="!showUpArrow()"
							></i>
						</slot>
					</th>
				</tr>
			</thead>
			<tbody class="table__body">
				<Fragment v-for="(item, index) in source.items" :key="index">
					<tr class="table__tr" :id="`${uid}_tr`" :key="index">
						<td
							class="table__td table__td-trigger"
							:class="
								collapsedRows.includes(index) ? 'is-open' : ''
							"
							@click="() => collapseRow(index)"
							ref="btnCollapse"
						>
							<i
								:class="
									!collapsedRows.includes(index)
										? 'icon icon-arrow-down-solid'
										: 'icon icon-arrow-up-solid'
								"
							></i>
						</td>
						<td
							:id="`${uid}_td`"
							:data-title="header.label"
							class="table__td"
							v-for="(header, index2) in sourceObject.headers"
							:key="index2"
						>
							<slot
								:name="`body-${header.key}`"
								:body="item"
								:index="index"
							>
								<span class="table__data">{{
									item[header.key]
								}}</span>
							</slot>
						</td>
					</tr>
					<tr v-show="collapsedRows.includes(index)">
						<td
							class="table__td table__td-collapsible-row"
							:colspan="sourceObject.headers.length + 1"
						>
							<slot :name="`expand-area${index}`"></slot>
						</td>
					</tr>
				</Fragment>
			</tbody>
		</table>
		<section
			class="table__pagination"
			aria-label="Pagination"
			v-if="hasPagination"
		>
			<Pagination v-model="pagination" :isArrowUp="isArrowUp" />
		</section>
	</div>
</template>

<script>
import Pagination from '@/components/shells/pagination/pagination';
import _ from 'lodash';
import { Fragment } from 'vue-fragment';

export default {
	name: 'Table',
	components: {
		Pagination,
		Fragment
	},
	props: {
		sourceObject: {
			type: Object,
			required: true
		},
		paginationFeature: {
			type: Boolean,
			default: true
		},
		tableMobilev1: {
			type: Boolean,
			default: false
		},
		tableMobilev2: {
			type: Boolean,
			default: false
		},
		tableNested: {
			type: Boolean,
			default: false
		},
		isArrowUp: {
			type: Boolean,
			default: false
		},
		editable: {
			type: Boolean,
			default: false
		},
		sortOptions: {
			type: Object,
			default: () => {}
		},
		collapse: {
			type: Boolean,
			default: false
		},
		locked: Boolean
	},
	data() {
		return {
			currentSort: '',
			currentSortDir: '',
			pagination: {},
			collapsedRows: []
		};
	},
	computed: {
		isTabletMode() {
			return true;
		},
		source() {
			return this.sourceObject.source || [];
		},
		hasPagination() {
			return (
				this.sourceObjectRightFormat &&
				this.sourceObject.source?.totalCount > 10
			);
		},
		sourceObjectRightFormat() {
			return (
				this.sourceObject &&
				'source' in this.sourceObject &&
				'headers' in this.sourceObject
			);
		}
	},
	methods: {
		collapseRow(index) {
			const indexEl = this.collapsedRows.findIndex(
				item => item === index
			);
			if (indexEl !== -1) {
				this.collapsedRows.splice(indexEl, 1);
				return;
			}

			this.collapsedRows.push(index);
			this.$emit('row-expanded', index);
		},
		sortBy(e) {
			e.stopPropagation();
			let sortBy = e.currentTarget.getAttribute('sortBy');
			let isSorteable = Boolean(
				e.currentTarget.getAttribute('isSorteable')
			);
			if (isSorteable) this.currentSort = sortBy;
			this.currentSortDir = isSorteable
				? this.currentSortDir === 'asc'
					? 'desc'
					: 'asc'
				: this.currentSortDir;
			return isSorteable
				? this.$emit('sort', {
						sortBy: this.currentSort,
						sortDir: this.currentSortDir
				  })
				: this.$emit('sort', { sortBy: null, sortDir: null });
		},
		showArrows(key) {
			return key === this.currentSort;
		},
		showUpArrow() {
			return this.currentSortDir === 'asc';
		}
	},
	watch: {
		sourceObject: {
			deep: true,
			immediate: true,
			handler() {
				if (this.sourceObjectRightFormat) {
					this.pagination = {
						page: this.sourceObject.source.page,
						countPerPage: this.sourceObject.source.countPerPage,
						totalCount: this.sourceObject.source.totalCount,
						totalPageCount: this.sourceObject.source.totalPageCount
					};
				}
			}
		},
		sortOptions: {
			deep: true,
			immediate: true,
			handler(val) {
				if (!_.isEmpty(val)) {
					this.currentSort = val.currentSort;
					this.currentSortDir = val.currentSortDir;
				}
			}
		},
		pagination: {
			deep: true,
			handler(val) {
				this.$emit('change', val);
			}
		}
	}
};
</script>
<style lang="scss">
.table-container {
	// UI/UX Note: please update
	&.is-locked {
		pointer-events: none;
	}
}

.is-selected {
	background: $ui-positive-light;
}
/**
    * Global Table
    * Can use SCSS variables = TRUE
 */
.table {
	width: 100%;
	border-collapse: collapse;
	border-spacing: 0;
	overflow: hidden;
	border-radius: $table-border-radius;
	text-align: left;
	font-size: $default-font-size - 2;
	color: $neutral-1;
	text-transform: capitalize;
	box-sizing: border-box;
	&.table--pagination-on {
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
	}
}
.table__header {
	white-space: nowrap;
	.table__tr {
		background: $table-header-bkgd;
	}
	.table__th {
		text-align: left;
		padding: $padding-sm;
		font-weight: $table-header-font-weight;
		font-size: $table-header-font-size;

		.icon {
			padding: 0 $padding-xs;
			vertical-align: middle;
		}
	}
}
.table__body {
	.table__tr {
		position: relative;
		&:nth-child(even) {
			background: $table-even-row-bkgd;

			&:hover {
				background: $neutral-3;
			}
		}
		&:nth-child(odd) {
			background: $table-odd-row-bkgd;

			&:hover {
				background: $neutral-3;
			}
		}
	}
}
.table__td,
.table__th {
	border: $table-border;
	padding: $padding-sm;
}

.table__data {
	word-break: break-all;
}
@include mq($until: md) {
	.table__header {
		.table__tr {
			display: none;
		}
	}
	.table__body {
		.table__tr {
			display: block;
			&:not(:last-child) {
				border-bottom: 2px solid $neutral-1;
			}
			.table__td {
				border-spacing: 0;
				display: grid;
				grid-template-columns: repeat(auto-fit, minmax(50px, 1fr));
				&[data-title]::before {
					content: attr(data-title);
					font-weight: bold;
				}
				&:last-child {
					border-bottom: 0;
				}
			}
		}
	}

	.table--mobile-v1 {
		.table__body .table__tr {
			.table__td:not(:first-child) {
				padding-left: $padding-xxl;
			}
			.table__td:first-child {
				background: $neutral-3;
				position: absolute;
				left: 0;
				top: 0;
				height: 100%;
				display: flex;
				&[data-title]::before {
					display: none;
				}
			}
		}
	}
	.table--mobile-v2 {
		.table__body .table__tr {
			.table__td {
				padding: 0;
				align-items: center;
				&[data-title]::before {
					background: $neutral-4;
					padding: $padding-md;
					margin-right: $gutter-lg;
					border-bottom: $table-border;
					border-bottom-color: $white;
					box-shadow: 2px 0 3px 0px rgba($neutral-3, 35%);
				}

				border-bottom: 0;
				border-top: 0;
				position: relative;
				&[data-title]::before {
					z-index: 1;
				}
				&:not(:last-child) {
					&[data-title]::before {
						border-bottom-color: $white;
					}
				}
				&:after {
					content: '';
					position: absolute;
					left: 0;
					right: 0;
					bottom: 0;
					background: $table-border-color;
					height: 1px;
				}
			}
			.table__td:first-child {
				display: none;
				&::before {
					display: none;
				}
			}
		}
	}
}

@include mq($from: md) {
	.table--mobile-v1 {
		.table__header .table__th:first-child {
			//span {display: none;}   // commenting out to show the column title in tablet and up. Will remove once real data has been populated
		}
		.table__body .table__tr {
			.table__td:first-child {
				width: 16px;
			}
		}
	}
}

/*------------------------------------*\
  #PAGINATION
\*------------------------------------*/
.table__pagination {
	width: 100%;
	border: $table-border;
	border-top: none;
	background: $neutral-4;
	border-bottom-left-radius: $table-border-radius;
	border-bottom-right-radius: $table-border-radius;
	padding: $padding-sm;
	@include mq($until: md) {
		border: none;
	}
}

/*------------------------------------*\
  #COLLAPSIBLE
\*------------------------------------*/

.table__td-trigger {
	width: 32px;
	text-align: center;
	background-color: $neutral-4;
	cursor: pointer;

	@include mq($from: md-l) {
		width: 48px;
	}
	&:hover {
		-o-outline: $focus-outline;
		-moz-outline: $focus-outline;
		outline: $focus-outline;
		outline-offset: $focus-offset;
	}

	.icon {
		color: $neutral-2;
		font-size: 16px;
		vertical-align: middle;
	}
}

.is-open {
	-o-outline: $focus-outline;
	-moz-outline: $focus-outline;
	outline: $focus-outline;
	outline-offset: $focus-offset;
}

.table__td-collapsible-row {
	background-color: $neutral-4;
	padding: $padding-sm 48px $padding-sm 40px;

	@include mq($from: md-l) {
		padding: $padding-sm 64px $padding-sm 56px;
	}
}

/*------------------------------------*\
  #NESTED
\*------------------------------------*/
.table--nested {
	border-radius: 0;

	.table__td,
	.table__th {
		border: none;
	}

	.table__header {
		.table__tr {
			background: transparent;
			border-bottom: solid 1px $white;
		}
	}
	.table__body {
		.table__tr {
			background: transparent;
			border-bottom: solid 1px $white;

			&:last-child {
				border-bottom: 0;
			}
		}
	}
}
</style>
